import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DialogData } from 'src/app/models/dialog-data';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { BeVeiculo } from 'src/app/models/BeVeiculo';
import { SubService } from 'src/app/services/sub.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { LevelAdm } from '@app/app-config';
import { AuthService } from '@app/services/auth.service';
import { TokenService } from '@app/services/token.service';
import { ListaDadosColuna } from 'src/app/components/lista-dados/lista-dados.component';
import { ModoExibicaoPersistidoService } from 'src/app/services/modo-exibicao-persistido.service';

type ModoExibicaoVeiculos = 'veiculos' | 'postos' | 'sem-usuario';

@Component({
  selector: 'app-veiculos',
  templateUrl: './list-veiculos.component.html',
  styleUrls: ['./list-veiculos.component.css']
})
export class ListVeiculosComponent implements OnInit, OnDestroy {
  public veiculos: BeVeiculo[] = [];
  public colunasListaDados: ListaDadosColuna[] = [
    {
      campo: 'dsMarca',
      rotulo: 'Marca',
      tamanho: '16%'
    },
    {
      campo: 'dsModelo',
      rotulo: 'Modelo',
      tamanho: '22%'
    },
    {
      campo: 'dsCor',
      rotulo: 'Cor',
      tamanho: '12%'
    },
    {
      campo: 'dsAnoFabricacao',
      rotulo: 'Ano Fabricação',
      tamanho: '14%'
    },
    {
      campo: 'dsAnoModelo',
      rotulo: 'Ano Modelo',
      tamanho: '14%'
    },
    {
      campo: 'dsPlaca',
      rotulo: 'Placa',
      tamanho: '14%'
    }
  ];
  public subscription?: Subscription;
  public modoExibicao: ModoExibicaoVeiculos = 'veiculos';

  private readonly chaveModoExibicao = 'veiculos.modoExibicao';

  public temVeiculoSemUsuario: boolean = false;
  public hasLevelAdm = false;

  public constructor(
    private httpClient: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private sub: SubService,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private tokenService: TokenService,
    private modoExibicaoPersistidoService: ModoExibicaoPersistidoService,
  ) {
  }

  public ngOnInit(): void {
    if (this.modoExibicaoPersistidoService.deveRestaurarModoExibicao()) {
      const modoExibicao = sessionStorage.getItem(this.chaveModoExibicao);
      if (modoExibicao === 'veiculos' || modoExibicao === 'postos' || modoExibicao === 'sem-usuario')
        this.modoExibicao = modoExibicao;
    }
    else
      sessionStorage.setItem(this.chaveModoExibicao, 'veiculos');
    this.subscription = this.httpClient.get<BeVeiculo[]>(
      `/api/Veiculos`
    ).subscribe({
      next: (res) => {
        this.unsubscribe();
        this.onResult(res);
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
      }
    });
    this.checkPermissions();
  }

  private checkPermissions(): void {
    this.tokenService.getTokenUserCredentials().subscribe(tokenStr => {
      if (tokenStr) {
        this.tokenService.decodeToken(tokenStr).subscribe(token => {
          if (token)
            this.hasLevelAdm = AuthService.canTokenActivate(token, LevelAdm);
        });
      } else
        this.hasLevelAdm = false;
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public onCreate() {
    this.router.navigate(['/novo-veiculo'], { relativeTo: this.route });
  }

  public exibirVeiculosSemUsuario(): void {
    this.modoExibicao = 'sem-usuario';
    sessionStorage.setItem(this.chaveModoExibicao, this.modoExibicao);
  }

  public exibirVeiculos(): void {
    this.modoExibicao = 'veiculos';
    sessionStorage.setItem(this.chaveModoExibicao, this.modoExibicao);
  }

  public exibirPostos(): void {
    this.modoExibicao = 'postos';
    sessionStorage.setItem(this.chaveModoExibicao, this.modoExibicao);
  }

  public onEdit(veiculo: BeVeiculo) {
    this.router.navigate(['/veiculo', veiculo.idVeiculo], { relativeTo: this.route });
  }

  public onDelete(veiculo: BeVeiculo) {
    this.showDialog('Deseja excluir esse registro?', 'Alerta', 'Não', 'Sim', veiculo);
  }

  public showDialog(msg: string, statusMsg: string, button: string = 'OK', secondButton: string = '', veiculo: BeVeiculo = {} as BeVeiculo) {
    if (this.subscription)
      return;
    const dialogData: DialogData = {
      title: statusMsg,
      message: msg,
      buttonText: button,
      secondButtonText: secondButton,
    };
    const dialogRef = this.dialog.open(DialogComponent, { data: dialogData });
    dialogRef.afterClosed().subscribe((result) => {
      this.unsubscribe();
      if (result) {
        this.sub.reset(this.httpClient.delete(`/api/Veiculos/${veiculo.idVeiculo}`),
          () => this.onDeleteConfirm(veiculo),
          () => this.showDialog('Não foi possível excluir o registro!', 'Erro')
        );
      }
    })
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  private onResult(res: BeVeiculo[]) {
    this.veiculos = res ?? [];
  }

  private onDeleteConfirm(res: BeVeiculo) {
    this.veiculos = this.veiculos.filter(veiculo => veiculo.idVeiculo !== res.idVeiculo);
    this.snackBarService.exibirSucesso('Registro excluído!');
  }
}
